import type {
    ComposableOptionsBase,
    SearchRequestParams,
    SearchResults
} from '~/types';
import type { NitroFetchOptions } from 'nitropack';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';


export const searchQueryMaxLength = 100;

export interface UseSearchOptions extends ComposableOptionsBase {}
export function useSearch(options: Partial<UseSearchOptions> = {}) {
    const apiBasePath = `/v1/search`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    function getEmptyResults(query: string = ''): SearchResults {
        return {
            query,
            skus: [],
            products: [],
            collections: [],
            pages: []
        };
    }

    // Mirrors what the API does to `q` (truncate, then strip), so callers can
    // show the term that was actually searched and skip the request when
    // nothing is left. Truncate by code points rather than UTF-16 units so an
    // emoji at the limit is dropped whole instead of leaving a lone surrogate,
    // which would throw during URI encoding.
    function normalizeQuery(q: Nilish<string>): string {
        return Array.from(q ?? '').slice(0, searchQueryMaxLength).join('').trim();
    }

    async function search(
        q: Nilish<string>,
        params: Omit<SearchRequestParams, 'q'> = {}
    ): Promise<SearchResults | Error> {
        const query = normalizeQuery(q);

        if (!query) {
            return getEmptyResults();
        }

        const url = `${apiBasePath}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { q: query, ...params }
        };

        try {
            return await $_fetch<SearchResults>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    return {
        search,
        normalizeQuery,
        getEmptyResults
    };
}
